/**
 * Session activity tracking for security and analytics
 */

interface SessionActivity {
  lastActivity: number;
  lastPath: string;
  activityCount: number;
  startTime: number;
}

class SessionActivityTracker {
  private static STORAGE_KEY = 'rulevia_session_activity';
  private static ACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes
  private static CHECK_INTERVAL = 60 * 1000; // 1 minute
  
  private activity: SessionActivity | null = null;
  private intervalId: NodeJS.Timeout | null = null;
  private warningCallback: ((minutesLeft: number) => void) | null = null;
  private timeoutCallback: (() => void) | null = null;

  /**
   * Start tracking session activity
   */
  start(sessionId: string) {
    console.log('📊 Starting session activity tracking');
    
    // Load existing activity or create new
    const stored = this.loadActivity();
    
    if (stored && stored.sessionId === sessionId) {
      this.activity = stored.activity;
    } else {
      this.activity = {
        lastActivity: Date.now(),
        lastPath: window.location.pathname,
        activityCount: 0,
        startTime: Date.now()
      };
    }
    
    // Save initial state
    this.saveActivity(sessionId);
    
    // Set up activity listeners
    this.setupListeners();
    
    // Start checking for inactivity
    this.startInactivityCheck();
  }

  /**
   * Stop tracking session activity
   */
  stop() {
    console.log('📊 Stopping session activity tracking');
    
    // Clear listeners
    this.removeListeners();
    
    // Clear interval
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    
    // Clear activity
    this.activity = null;
    localStorage.removeItem(SessionActivityTracker.STORAGE_KEY);
  }

  /**
   * Update activity timestamp
   */
  updateActivity(path?: string) {
    if (!this.activity) return;
    
    this.activity.lastActivity = Date.now();
    this.activity.activityCount++;
    
    if (path) {
      this.activity.lastPath = path;
    }
    
    // Save to storage
    const sessionId = this.getCurrentSessionId();
    if (sessionId) {
      this.saveActivity(sessionId);
    }
  }

  /**
   * Set warning callback for inactivity warnings
   */
  onInactivityWarning(callback: (minutesLeft: number) => void) {
    this.warningCallback = callback;
  }

  /**
   * Set timeout callback for session timeout
   */
  onSessionTimeout(callback: () => void) {
    this.timeoutCallback = callback;
  }

  /**
   * Get current session analytics
   */
  getAnalytics() {
    if (!this.activity) return null;
    
    const now = Date.now();
    const sessionDuration = now - this.activity.startTime;
    const timeSinceLastActivity = now - this.activity.lastActivity;
    
    return {
      sessionDuration,
      timeSinceLastActivity,
      activityCount: this.activity.activityCount,
      lastPath: this.activity.lastPath,
      isActive: timeSinceLastActivity < SessionActivityTracker.ACTIVITY_TIMEOUT
    };
  }

  private setupListeners() {
    // Track user interactions
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, this.handleActivity, { passive: true });
    });
    
    // Track navigation
    window.addEventListener('popstate', this.handleNavigation);
  }

  private removeListeners() {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.removeEventListener(event, this.handleActivity);
    });
    
    window.removeEventListener('popstate', this.handleNavigation);
  }

  private handleActivity = () => {
    this.updateActivity();
  };

  private handleNavigation = () => {
    this.updateActivity(window.location.pathname);
  };

  private startInactivityCheck() {
    this.intervalId = setInterval(() => {
      if (!this.activity) return;
      
      const timeSinceLastActivity = Date.now() - this.activity.lastActivity;
      const timeRemaining = SessionActivityTracker.ACTIVITY_TIMEOUT - timeSinceLastActivity;
      
      // Warning at 5 minutes left
      if (timeRemaining > 0 && timeRemaining <= 5 * 60 * 1000) {
        const minutesLeft = Math.ceil(timeRemaining / 60000);
        
        if (this.warningCallback && minutesLeft > 0) {
          this.warningCallback(minutesLeft);
        }
      }
      
      // Timeout
      if (timeRemaining <= 0) {
        console.log('⏰ Session timeout due to inactivity');
        
        if (this.timeoutCallback) {
          this.timeoutCallback();
        }
        
        this.stop();
      }
    }, SessionActivityTracker.CHECK_INTERVAL);
  }

  private saveActivity(sessionId: string) {
    if (!this.activity) return;
    
    const data = {
      sessionId,
      activity: this.activity,
      timestamp: Date.now()
    };
    
    try {
      localStorage.setItem(SessionActivityTracker.STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Failed to save session activity:', error);
    }
  }

  private loadActivity(): { sessionId: string; activity: SessionActivity } | null {
    try {
      const stored = localStorage.getItem(SessionActivityTracker.STORAGE_KEY);
      if (!stored) return null;
      
      const data = JSON.parse(stored);
      
      // Check if data is recent (within last hour)
      if (Date.now() - data.timestamp > 60 * 60 * 1000) {
        localStorage.removeItem(SessionActivityTracker.STORAGE_KEY);
        return null;
      }
      
      return data;
    } catch (error) {
      console.error('Failed to load session activity:', error);
      return null;
    }
  }

  private getCurrentSessionId(): string | null {
    // Get session ID from auth token or generate one
    const authToken = localStorage.getItem('rulevia-auth-token');
    if (!authToken) return null;
    
    try {
      const tokenData = JSON.parse(authToken);
      return tokenData.access_token?.substring(0, 8) || null;
    } catch {
      return null;
    }
  }
}

// Export singleton instance
export const sessionActivity = new SessionActivityTracker();